import axios from 'axios';
import { supabase } from './supabase';
import { noteApiWrite } from './query/lookupCache';

const apiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3100';

export const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Automatically attach the current Supabase JWT to every outbound request.
// `getSession()` returns the cached token without a network call; Supabase's
// auth client handles background refresh automatically.
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

// 401 interceptor — refresh + single retry, then sign out (C3c)
//
// A 401 can happen if the JWT expired between the request being queued and
// the API validating it.  We attempt a session refresh and replay the original
// request once.  Only if the refresh also fails do we sign out and redirect.
api.interceptors.response.use(
  (response) => {
    noteApiWrite(response.config.method, response.config.url);

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retried) {
      originalRequest._retried = true;

      try {
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !session?.access_token) throw refreshError ?? new Error('No session');

        // Retry the original request with the refreshed token
        originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
        return api(originalRequest);
      } catch {
        // Refresh failed — session is truly expired; sign out and redirect
        await supabase.auth.signOut({ scope: 'local' });
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  },
);
